<template>
  <slot></slot>
  <modal :id="modalId" :title="title">
    <div role="form">

      <div v-if="deleting" class="text-center">
        <p>Are you sure you want to delete <em>'{{ date }}'</em>?</p>
      </div>
      <div v-else>

        <!-- Date -->
        <div class="form-group row">
          <label for="date" class="col-sm-2 col-form-label">Follow-up date:</label>
          <div class="col-sm-5">

            <div v-if="isNew || reScheduleMode" class="date">
              <datepicker
                v-model="date"
                :min-date="minDate"
                name="date">
              </datepicker>
              <button v-if="reScheduleMode" class="btn btn-default btn-xs" @click="cancelReschedule()">Cancel</button>
            </div>

            <div v-else>
              <input type="hidden" name="date" :value="date" />
              <strong>{{ date }}</strong>
              <button class="btn btn-default btn-sm" @click="reschedule()">{{ completed ? 'Re-schedule' : 'Change' }}</button>
            </div>

          </div>
          <div v-if="!isNew" class="col-sm-5 text-right" v-html="data.rendered_status"></div>
        </div>

        <!-- Subject -->
        <div class="form-group row">
          <label for="subject" class="col-sm-2 col-form-label">Subject:</label>
          <div class="col-sm-10">
            <input type="text" name="subject" class="form-control" v-model="data.subject">
          </div>
        </div>

        <!-- Note -->
        <div class="form-group row">
          <label for="note" class="col-sm-2 col-form-label">Note:</label>
          <div class="col-sm-10">
            <textarea name="note" class="form-control" rows="10" v-model="data.note"></textarea>
          </div>
        </div>

      </div>

      <input type="hidden" name="_method" :value="formMethod">
      <input type="hidden" name="_token" :value="$csrf_token">
    </div>

    <div v-if="error" class="alert alert-danger text-center mt-4 mb-0">{{ error }}</div>

    <template #footer>

      <template v-if="deleting">
        <button type="button" class="btn btn-default" @click.prevent="cancelDeleting"><i class="fa fa-fw fa-ban"></i> Cancel</button>
        <button type="submit" class="btn btn-danger" @click.prevent="destroy" :disabled="blockButtons">
          <i class="fa fa-fw fa-trash"></i> Delete
          <i class="fa fa-spinner fa-spin" v-if="deleteIsProcessing"></i>
        </button>
      </template>

      <template v-else>
        <button type="button" class="btn btn-danger mr-auto" v-if="isExisting && canDelete" @click.prevent="confirmDeleting"><i class="fa fa-fw fa-trash"></i> Delete</button>
        <button type="button" class="btn btn-default" @click="hideModal"><i class="fa fa-fw fa-ban"></i> Close</button>
        <button type="submit" class="btn btn-primary" :disabled="isEmpty || blockButtons" @click.prevent="save">
          <i class="fa fa-fw fa-upload"></i> Save changes
          <i class="fa fa-spinner fa-spin" v-if="saveIsProcessing"></i>
        </button>

        <template v-if="!completed && !reScheduleMode && isExisting">
          <button type="button" class="btn btn-success" @click.prevent="complete" :disabled="blockButtons">
            <i class="fa fa-fw fa-check"></i> Complete
            <i class="fa fa-spinner fa-spin" v-if="completeIsProcessing"></i>
          </button>
        </template>
      </template>

    </template>

  </modal>

</template>

<script>
import HasModal from "../mixins/HasModal";

export default {
  mixins: [HasModal],

  props: {

    id: {
      type: Number,
      default: 0
    },
    adjusterId: {
      type: Number,
      required: true
    },
    // Custom id for modal component.
    modalId: {
      type: String,
      default: 'adjuster-followup-modal'
    },
    formMethod: {
      type: String,
      default: 'POST'
    },
    initialDate: {
      type: String,
      default: ''
    },
    subject: {
      type: String,
      default: ''
    },
    note: {
      type: String,
      default: ''
    },
    completed: {
      type: Boolean,
      default: false
    },
    ready: {
      type: Boolean,
      default: false
    },
    canDelete: {
      type: Boolean,
      default: false
    }
  },

  data() {

    return {
      date: this.initialDate,
      error: '',
      completeIsProcessing: false,
      deleteIsProcessing: false,
      saveIsProcessing: false,
      reScheduleMode: false,
      blockButtons: false,
      deleting: false,
      data: {
        subject: this.subject,
        note: this.note
      },
    };

  },
  computed: {
    title() {
      if (this.deleting) return 'Delete Follow-Up Date';
      return this.isNew ? "Add Follow-Up Date" : "Follow-Up";
    },
    hasData() {
      return !!(this.date && this.date.trim().length);
    },
    isEmpty() {
      return !this.hasData;
    },
    isExisting() {
      return this.id > 0;
    },
    isNew() {
      return !this.isExisting;
    },
    minDate() {
      return moment(new Date()).set({hour:0,minute:0,second:0,millisecond:0}).format('MM/dd/yyyy');
    }
  },
  methods: {
    save() {
      let action = '/adjusters/' + this.adjusterId + '/followups';
      if (!this.isNew) action += '/' + this.id;

      const formData = new FormData();
      formData.append('_method', this.isNew ? "POST" : "PATCH");
      formData.append('date', this.date);
      formData.append('subject', this.data.subject);
      formData.append('note', this.data.note);

      this.blockButtons = true;
      this.saveIsProcessing = true;
      axios.post(action, formData)
          .then(response => {
            this.hideModal();
            window.location.reload();
          })
          .catch(error => {
            this.error = 'Unable to save the follow-up';
            this.blockButtons = false;
          })
          .finally(() => {
            this.saveIsProcessing = false;
          });
    },

    complete() {
      const formData = new FormData();
      formData.append('_method', "PATCH");

      this.blockButtons = true;
      this.completeIsProcessing = true;
      axios.post('/adjusters/' + this.adjusterId + '/followups/' + this.id + '/complete', formData)
        .then(response => {
          this.hideModal();
          window.location.reload();
        })
        .catch(error => {
          this.error = 'Unable to complete the follow-up';
          this.blockButtons = false;
        })
        .finally(() => {
          this.completeIsProcessing = false;
        });
    },

    destroy() {
      const formData = new FormData();
      formData.append('_method', "DELETE");

      this.blockButtons = true;
      this.deleteIsProcessing = true;
      axios.post('/adjusters/' + this.adjusterId + '/followups/' + this.id, formData)
          .then(response => {
            this.hideModal();
            window.location.reload();
          })
          .catch(error => {
            this.error = 'Unable to delete the follow-up';
            this.blockButtons = false;
          })
          .finally(() => {
            this.deleteIsProcessing = false;
          });
    },

    reschedule() {
      this.reScheduleMode = true;
      this.date = this.ready ? '' : this.initialDate;
    },

    cancelReschedule() {
      this.reScheduleMode = false;
      this.date = this.initialDate;
    },

    confirmDeleting() {
      this.deleting = true;
    },

    cancelDeleting() {
      this.deleting = false;
    },
  }
}

</script>
